const HEX_RE = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i
const SHORT_HEX_RE = /^#?([a-f\d])([a-f\d])([a-f\d])$/i

export const hexToRgb = (hex) => {
  const value = String(hex || '')
  const short = SHORT_HEX_RE.exec(value)
  const full = short
    ? `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`
    : value
  const result = HEX_RE.exec(full)
  if (!result) return null
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  }
}

export const rgbToHex = (r, g, b) =>
  `#${[r, g, b]
    .map((channel) => {
      const hex = Math.round(Math.max(0, Math.min(255, channel))).toString(16)
      return hex.length === 1 ? `0${hex}` : hex
    })
    .join('')}`

export const mix = (hex1, hex2, weight = 0.5) => {
  const c1 = hexToRgb(hex1)
  const c2 = hexToRgb(hex2)
  if (!c1 || !c2) return hex1 || hex2
  return rgbToHex(
    c1.r * (1 - weight) + c2.r * weight,
    c1.g * (1 - weight) + c2.g * weight,
    c1.b * (1 - weight) + c2.b * weight
  )
}

export const darken = (hex, amount = 0.15) => mix(hex, '#000000', amount)

export const lighten = (hex, amount = 0.15) => mix(hex, '#ffffff', amount)

export const withAlpha = (hex, alpha = 0.1) => {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`
}

/** WCAG relative luminance, used to pick readable foreground text on a brand fill. */
export const relativeLuminance = (hex) => {
  const rgb = hexToRgb(hex)
  if (!rgb) return 1
  const channel = (raw) => {
    const c = raw / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b)
}

export const contrastRatio = (hex1, hex2) => {
  const l1 = relativeLuminance(hex1)
  const l2 = relativeLuminance(hex2)
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)
  return (lighter + 0.05) / (darker + 0.05)
}

/** Returns whichever of white/near-black meets AA best against `hex`. */
export const readableTextOn = (hex, dark = '#1f2937', light = '#ffffff') =>
  contrastRatio(hex, light) >= contrastRatio(hex, dark) ? light : dark
